import { DateTime } from 'luxon';
import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { COLOR_GANT_CALENDAR_SELECTION } from '../../const';
import { getDateScaled } from '../../functions';
import { useSettings } from '../../hooks/useSettings';
import { RootState } from '../../store';
import { setterCalendar } from '../../store/slices/calendarSlice';
import { setterGantDraggable } from '../../store/slices/gantDraggableSlice';
import { setterGant } from '../../store/slices/gantSlice';
import { useGantGridDatePeriodMetrics } from './useGantGridDatePeriodMetrics';

interface GantGridCalendarSelectedGrabProps {
    typeGrid: 'header' | 'main';
}

export const GantGridDatePeriodDragMarker: React.FC<GantGridCalendarSelectedGrabProps> = ({ typeGrid }) => {
    const dispatch = useDispatch();
    const startDateExtend = useSelector<RootState, string>((state) => state.gant.startDateExtend);
    const offsetDate = useSelector<RootState, string>((state) => state.gant.currentOffsetDate);
    const clickDelta = useRef<number>(0);

    const { x1, width, height, isDragging, widthColumn, dragX, scheduleScale, isComponentVisible } =
        useGantGridDatePeriodMetrics(typeGrid);

    const { saveUserSettings } = useSettings();

    if (!isComponentVisible) {
        return null;
    }

    const onPointerDown = (e: React.PointerEvent) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        if (!isDragging) {
            clickDelta.current = e.clientX - x1;
            dispatch(setterGantDraggable({ dragging: 'calendarDate', currentPeriodPositionX: x1 }));
        }
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (isDragging) {
            dispatch(setterGantDraggable({ currentPeriodPositionX: e.clientX - clickDelta.current }));
        }
    };

    const onPointerUp = (e: React.PointerEvent) => {
        e.currentTarget.releasePointerCapture(e.pointerId);
        if (isDragging) {
            const timelineStart = DateTime.fromISO(startDateExtend);
            const daysOffset = Math.round(dragX / widthColumn);

            const correspondingDate = timelineStart.plus({ days: daysOffset });
            const dateScaled = getDateScaled(correspondingDate, scheduleScale);

            const calendarOffsetDays = dateScaled.diff(DateTime.fromISO(offsetDate), 'days').days;
            dispatch(setterGantDraggable({ dragging: 'none' }));
            dispatch(
                setterGant({
                    calendarOffsetDays,
                }),
            );
            dispatch(
                setterCalendar({
                    currentDate: dateScaled.toISODate() ?? undefined,
                }),
            );
            saveUserSettings({
                calendarOffsetDays,
            });
        }
    };

    return (
        <g
            style={{ mixBlendMode: 'normal', cursor: 'grab' }}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerMove={onPointerMove}
        >
            <rect x={x1} y={0} width={width} height={height} fill={COLOR_GANT_CALENDAR_SELECTION} opacity={0} />
        </g>
    );
};
